import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Scale } from 'lucide-react';
import { useMyDisputes, useOpenDispute } from '@/api/feedback';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import type { DisputeStatus, Guid, RatingSummaryDto, ReviewDto, ReviewListDto } from '@/api/types';
import { Badge, type Tone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Field, FormError, Textarea } from '@/components/ui/Field';
import { formatDate } from '@/lib/format';
import { errorMessage } from '@/lib/errors';
import { cn } from '@/lib/cn';

/** Five stars, filled up to the rounded value (display only). */
export function Stars({ value, className }: { value: number; className?: string }) {
  const { t } = useTranslation();
  const filled = Math.round(value);
  return (
    <span className={cn('inline-flex items-center gap-0.5', className)} role="img" aria-label={t('feedback.starsLabel', { value })}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={cn('size-3.5', i <= filled ? 'fill-check text-check' : 'text-line-strong')} aria-hidden />
      ))}
    </span>
  );
}

/** "★ 4.5 (12)" — or a quiet "no ratings yet". */
export function RatingBadge({ rating, className, label }: { rating: RatingSummaryDto; className?: string; label?: string }) {
  const { t, i18n } = useTranslation();
  if (rating.count === 0 || rating.average === null)
    return <span className={cn('text-xs text-ink-3', className)}>{label ? `${label}: ` : ''}{t('feedback.noRating')}</span>;
  const avg = rating.average.toLocaleString(i18n.language.startsWith('en') ? 'en-US' : 'tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs text-ink-2', className)} title={t('feedback.ratingTitle', { avg, count: rating.count })}>
      {label && <span className="text-ink-3">{label}</span>}
      <Star className="size-3.5 fill-check text-check" aria-hidden />
      <span className="font-semibold text-ink">{avg}</span>
      <span className="text-ink-3">({rating.count})</span>
    </span>
  );
}

/** Accessible 1–5 star picker (a radio group). */
export function StarInput({ value, onChange, label }: { value: number; onChange: (v: number) => void; label: string }) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div role="radiogroup" aria-label={label} className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          role="radio"
          aria-checked={value === i}
          aria-label={t('feedback.starsOption', { count: i })}
          onMouseEnter={() => setHover(i)}
          onClick={() => onChange(i)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); onChange(Math.min(5, (value || 0) + 1)); }
            if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); onChange(Math.max(1, (value || 2) - 1)); }
          }}
          tabIndex={value === i || (value === 0 && i === 1) ? 0 : -1}
          className="rounded p-0.5"
        >
          <Star className={cn('size-7 transition-colors', i <= shown ? 'fill-check text-check' : 'text-line-strong')} aria-hidden />
        </button>
      ))}
      <span className="ml-2 text-sm text-ink-2">{shown ? t(`feedback.starWord.${shown}`) : ''}</span>
    </div>
  );
}

/** Write or edit a review (stars + optional comment). */
export function ReviewDialog({ open, onOpenChange, title, description, initial, onSubmit, loading }: {
  open: boolean; onOpenChange: (o: boolean) => void; title: string; description?: string; initial?: ReviewDto | null;
  onSubmit: (stars: number, comment: string | null) => Promise<unknown>; loading: boolean;
}) {
  const { t } = useTranslation();
  const [stars, setStars] = useState(initial?.stars ?? 0);
  const [comment, setComment] = useState(initial?.comment ?? '');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setStars(initial?.stars ?? 0);
      setComment(initial?.comment ?? '');
      setError(null);
    }
  }, [open, initial]);
  const submit = async () => {
    if (stars < 1) { setError(t('feedback.pickStars')); return; }
    try {
      await onSubmit(stars, comment.trim() || null);
      onOpenChange(false);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      footer={<><Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button><Button variant="primary" loading={loading} onClick={() => void submit()}>{t('feedback.send')}</Button></>}
    >
      <div className="flex flex-col gap-4">
        <FormError message={error ?? undefined} />
        <StarInput value={stars} onChange={setStars} label={t('feedback.stars')} />
        <Field label={t('feedback.comment')} hint={t('feedback.commentHint')}>
          {(p) => <Textarea {...p} rows={4} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}

export function ReviewItem({ review, showAuthor = true }: { review: ReviewDto; showAuthor?: boolean }) {
  const { i18n } = useTranslation();
  return (
    <li className="flex flex-col gap-1 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Stars value={review.stars} />
        {showAuthor && (
          <span className="font-medium">
            {review.direction === 'CustomerToTester' ? review.organizationName : review.authorTeam ?? review.authorName}
          </span>
        )}
        <span className="text-xs text-ink-3">{review.cycleName} · {formatDate(review.updatedAt, i18n.language)}</span>
      </div>
      {review.comment && <p className="text-ink-2">“{review.comment}”</p>}
    </li>
  );
}

export function ReviewList({ list, emptyText }: { list: ReviewListDto; emptyText: string }) {
  return list.items.length === 0 ? (
    <p className="p-4 text-sm text-ink-3">{emptyText}</p>
  ) : (
    <ul className="divide-y divide-line">{list.items.map((r) => <ReviewItem key={r.id} review={r} />)}</ul>
  );
}

const DISPUTE_TONE: Record<DisputeStatus, Tone> = {
  Open: 'check', Escalated: 'info', ResolvedByCustomer: 'trusted', Overturned: 'trusted', Upheld: 'suspicious', Withdrawn: 'muted',
};

export function DisputeStatusBadge({ status }: { status: DisputeStatus }) {
  const { t } = useTranslation();
  return <Badge tone={DISPUTE_TONE[status]}>{t(`disputeStatus.${status}`)}</Badge>;
}

/** The tester explains why a decision is wrong. */
export function DisputeDialog({ open, onOpenChange, subject, onSubmit, loading }: {
  open: boolean; onOpenChange: (o: boolean) => void; subject: string; onSubmit: (reason: string) => Promise<unknown>; loading: boolean;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) { setReason(''); setError(null); }
  }, [open]);
  const submit = async () => {
    if (!reason.trim()) { setError(t('disputes.reasonRequired')); return; }
    try {
      await onSubmit(reason.trim());
      onOpenChange(false);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('disputes.openTitle', { subject })}
      description={t('disputes.openHint')}
      footer={<><Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button><Button variant="primary" loading={loading} onClick={() => void submit()}>{t('disputes.send')}</Button></>}
    >
      <div className="flex flex-col gap-3">
        <FormError message={error ?? undefined} />
        <Field label={t('disputes.reason')} required hint={t('disputes.reasonHint')}>
          {(p) => <Textarea {...p} rows={5} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}

/** For a rejected run or bug: the dispute's state, or a button to open one. */
export function DisputeAction({ executionId, bugId, subject }: { executionId?: Guid | null; bugId?: Guid | null; subject: string }) {
  const { t } = useTranslation();
  const disputes = useMyDisputes();
  const open = useOpenDispute();
  const [dialog, setDialog] = useState(false);
  const existing = disputes.data?.find((d) => (executionId && d.executionId === executionId) || (bugId && d.bugId === bugId));
  if (disputes.isPending) return null;
  if (existing)
    return (
      <Link to="/my/disputes" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-xs text-ink-2 hover:underline">
        {t('disputes.label')}: <DisputeStatusBadge status={existing.status} />
      </Link>
    );
  return (
    <>
      <Button size="xs" variant="ghost" icon={<Scale className="size-3.5" />} onClick={(e) => { e.stopPropagation(); setDialog(true); }}>{t('disputes.open')}</Button>
      <DisputeDialog open={dialog} onOpenChange={setDialog} subject={subject} loading={open.isPending}
        onSubmit={(reason) => open.mutateAsync({ executionId: executionId ?? null, bugId: bugId ?? null, reason }).then(() => toast.success(t('disputes.opened')))} />
    </>
  );
}
